import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { Message } from 'semantic-ui-react';
import { useLocation } from '@reach/router';

import { useMe } from '$/hooks/useMe';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import { useNextPaymentDueDate } from '$/hooks/useNextPaymentDueDate';

import {
  SubscriptionStatus,
  SubscriptionSubstatus,
  TeamRegistrantLimit,
} from '$/GraphQL/schema/schema';
import { usePlanPageCTAButtons } from '$/sections/Home/Profile/PlanPage/PlanPageCTAButtons';

import { PermissionedLink } from '../Router';
import { ToastMessageType, useToast } from '../Toast/Toast';
import { rolesPermissionsMatrix } from '$/shared/frontend/rolesPermissionsMatrix';

const SuspendedAlertBar: React.FC<{ className?: string; children: React.ReactNode }> = ({
  children,
  className,
}) => (
  <div
    className={className}
    css={css`
      a {
        ${tw`text-white font-bold underline hover:underline hover:text-white cursor-pointer`};
      }
    `}
  >
    {children}
  </div>
);

const PastDueAlert: React.FC = () => {
  const { nextPaymentDueDateText } = useNextPaymentDueDate();
  return (
    <p>
      We were unable to charge your credit card and renew your monthly subscription. Please update
      your payment details by {nextPaymentDueDateText} to keep your account active.
    </p>
  );
};

const PastDueToastAlert: React.FC<{ id?: string }> = ({ id }) => {
  const { hide } = useToast();
  const me = useMe();
  React.useEffect(() => {
    if (me?.team?.subscriptionStatus !== SubscriptionStatus.PastDue && id) {
      hide(id);
    }
  }, [hide, id, me]);
  return (
    <SuspendedAlertBar>
      We were unable to renew your monthly subscription to eWebinar.{' '}
      <PermissionedLink to={rolesPermissionsMatrix.accountSubscription.path}>
        Update payment details now
      </PermissionedLink>
    </SuspendedAlertBar>
  );
};

const CancelledAlert: React.FC = () => {
  const { nextPaymentDueDateText } = useNextPaymentDueDate();
  return (
    <p>
      Your subscription has been canceled and will end on {nextPaymentDueDateText}. At that time,
      you webinars will be unpublished. Please resubscribe before that time to keep your account
      active.
    </p>
  );
};

const CancelledToastAlert: React.FC = () => {
  const { nextPaymentDueDateText } = useNextPaymentDueDate();
  return (
    <SuspendedAlertBar>
      Your subscription will end and your webinars will be unpublished on {nextPaymentDueDateText}.{' '}
      <PermissionedLink to={rolesPermissionsMatrix.accountSubscription.path}>
        Resubscribe now
      </PermissionedLink>
    </SuspendedAlertBar>
  );
};

const RegistrantLimitAlert: React.FC<{ registrantLimit: Partial<TeamRegistrantLimit> }> = ({
  registrantLimit,
}) => {
  if (registrantLimit.isFull) {
    return (
      <SuspendedAlertBar>
        <span>You've hit your registrant limit!</span>{' '}
        <PermissionedLink className='ml-1/4e' to={rolesPermissionsMatrix.accountSubscription.path}>
          See details
        </PermissionedLink>
      </SuspendedAlertBar>
    );
  }
  if (registrantLimit.registrantsLeftPercent && registrantLimit.registrantsLeftPercent <= 20) {
    return (
      <SuspendedAlertBar>
        <span>You are nearing your registrant limit!</span>{' '}
        <PermissionedLink className='ml-1/4e' to={rolesPermissionsMatrix.accountSubscription.path}>
          See details
        </PermissionedLink>
      </SuspendedAlertBar>
    );
  }
  return null;
};

export const SuspendedAlert: React.FC<{ showOnToast?: boolean }> = ({ showOnToast }) => {
  const me = useMe();
  const location = useLocation();
  const { hide, show } = useToast();
  const { buttons } = usePlanPageCTAButtons();

  const toastIdRef = React.useRef<string | null>(null);
  const { subscriptionStatus } = me?.team ?? {
    subscriptionStatus: undefined,
    nextPaymentDueDate: undefined,
  };
  const subscriptionStatusChanged = useHasChangedShallow([subscriptionStatus]);
  const isDelinquent = me.team?.subscriptionSubstatus === SubscriptionSubstatus.ChurnedDelinquent;
  const isSuspended = [SubscriptionStatus.Suspended, SubscriptionStatus.Incomplete].includes(
    me.team.subscriptionStatus
  );

  const showRegistrantLimitAlert =
    me.team.registrantLimit.isFull || me.team.registrantLimit.registrantsLeftPercent <= 20;

  React.useEffect(() => {
    if (location.pathname === rolesPermissionsMatrix.accountSubscription.path) {
      toastIdRef.current && hide(toastIdRef.current);
    }
  }, [hide, location.pathname]);

  React.useEffect((): void => {
    if (
      location.pathname === rolesPermissionsMatrix.accountSubscription.path ||
      !showOnToast ||
      (!subscriptionStatusChanged && !showRegistrantLimitAlert)
    )
      return;

    if (subscriptionStatus === SubscriptionStatus.PastDue) {
      toastIdRef.current = show({
        type: ToastMessageType.Error,
        render: (message) => <PastDueToastAlert id={message.id} />,
        clickClose: true,
      });
      return;
    }

    if (subscriptionStatus === SubscriptionStatus.Cancelled) {
      toastIdRef.current = show({
        type: ToastMessageType.Error,
        render: () => <CancelledToastAlert />,
        clickClose: true,
      });
    }

    if (showRegistrantLimitAlert && !location.pathname.includes('/onboard')) {
      toastIdRef.current = show({
        type: ToastMessageType.Error,
        render: () => <RegistrantLimitAlert registrantLimit={me.team.registrantLimit} />,
        clickClose: true,
      });
    }
  }, [
    show,
    showOnToast,
    location.pathname,
    subscriptionStatus,
    subscriptionStatusChanged,
    me.team.registrantLimit,
    showRegistrantLimitAlert,
  ]);

  if (showOnToast) return null;

  let messageInfo: {
    header: string;
    content: React.ReactElement;
    cta?: React.ReactElement;
  } | null = null;

  if (isSuspended) {
    if (me.team.subscriptionSubstatus === SubscriptionSubstatus.Pending) {
      messageInfo = {
        header: `Pending Payment`,
        content: (
          <p>
            Your subscription is temporarily suspended pending payment completion from the bank
            attached to your credit card. Scroll down and click on any PENDING invoices to complete
            payment.
          </p>
        ),
      };
    } else {
      messageInfo = {
        header: `Reactivate your account`,
        content: isDelinquent ? (
          <p>
            Your subscription has been suspended due to non-payment after multiple failed attempts
            to charge your credit card. Please resubscribe and update your payment details to
            activate your account.
          </p>
        ) : (
          <p>Your subscription has been canceled. Please resubscribe to activate your account.</p>
        ),
        cta: buttons.restore,
      };
    }
  }

  if (me.team?.subscriptionSubstatus === SubscriptionSubstatus.PastDue) {
    messageInfo = {
      header: `Keep your account active`,
      content: <PastDueAlert />,
      cta: buttons.updatePaymentDetails,
    };
  }

  if (
    [SubscriptionSubstatus.ActiveCanceled, SubscriptionSubstatus.TrialingCanceled].includes(
      me.team?.subscriptionSubstatus
    )
  ) {
    messageInfo = {
      header: `Keep your account active`,
      content: <CancelledAlert />,
      cta: buttons.resubscribe,
    };
  }

  if (!messageInfo) return null;

  return (
    <Message error className='mb-4'>
      <div className='flex md:items-center flex-col md:flex-row'>
        <div className='flex flex-col flex-1'>
          <Message.Header className='mb-1/2e'>{messageInfo.header}</Message.Header>
          {messageInfo.content}
        </div>
        {messageInfo.cta && <div className='md:pl-1e'>{messageInfo.cta}</div>}
      </div>
    </Message>
  );
};
